import { z } from 'zod';

/**
 * Wire shape for HTTP error responses, per ADR 0013 (Backend error handling).
 *
 * - `id` is minted by the backend Fastify error hook and is present iff the
 *   HTTP status is >= 500 — it's the only handle a user has on a reported
 *   failure (rendered via ADR 0014's `<ErrorWithId>` on the FE).
 * - `code` is a stable `{module}.{entity}.{condition}` identifier the FE
 *   translates against `packages/i18n`. Present whenever the condition is
 *   stable enough to translate or branch on.
 * - `message` is en-US developer-facing copy authored at the throw site. It
 *   is consumed as-is by 3rd-party API consumers and MCP servers; the
 *   immoOS web client NEVER displays it to a user (ADR 0009 + ADR 0014).
 * - `details` carries ICU interpolation parameters keyed by the dictionary
 *   entry for `code` (e.g. `{ limit: 10 }`).
 * - `trace` is the original error's stack split into one entry per
 *   line — populated ONLY when the backend runs with
 *   `NODE_ENV=development`. Never present in production or test
 *   envelopes; the production hook never sets it and ADR 0013's "no
 *   stacks/SQL/PII on the wire" rule still holds outside dev.
 *   Arrays render legibly in dev tooling; the equivalent single
 *   string came out as one giant line of escaped `\n` characters.
 *
 * The HTTP status code rides on the response line, not in the body.
 */
export const WireErrorSchema = z.object({
  error: z.object({
    id: z.string().optional(),
    code: z.string().optional(),
    message: z.string().optional(),
    details: z.record(z.string(), z.unknown()).optional(),
    trace: z.array(z.string()).optional(),
  }),
});

export type WireError = z.infer<typeof WireErrorSchema>;

/**
 * Per ADR 0013 §wire shape, the api-contract-normalised projection of a
 * Zod issue carried under `details.issues` for `ValidationError` and the
 * `shared.request.invalid` boundary failure. The raw Zod
 * `path: (string | number)[]` is collapsed to a dotted string so consumers
 * have a stable, serialisable address; `code` is the Zod issue code (e.g.
 * `too_small`, `invalid_type`) and `message` is the en-US Zod message.
 */
export const ZodIssueSchema = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
});

export type WireZodIssue = z.infer<typeof ZodIssueSchema>;
